import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import api from "../../lib/api";
import { Search, Plus, Minus, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

export default function MenuBrowse() {
  const navigate = useNavigate();
  const { currentRoom, addToCart, cartCount, cartTotal } = useOutletContext();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [qtyModal, setQtyModal] = useState(null);
  const [qty, setQty] = useState(1);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (!currentRoom) navigate("/pos/room");
  }, [currentRoom, navigate]);

  const fetchCategories = useCallback(async () => {
    const res = await api.get("/pos/categories");
    setCategories(res.data);
  }, []);

  const fetchItems = useCallback(async () => {
    const params = {};
    if (activeCat !== "all") params.category_id = activeCat;
    if (search.length >= 2) params.search = search;
    const res = await api.get("/pos/items", { params });
    setItems(res.data);
  }, [activeCat, search]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchItems(), 300);
  };

  const tapItem = (item) => {
    addToCart(item, 1);
    toast.success(`${item.item_name} added`, { duration: 1500 });
  };

  const longPressItem = (item) => {
    setQtyModal(item); setQty(1);
  };

  const confirmQty = () => {
    if (qtyModal && qty > 0) {
      addToCart(qtyModal, qty);
      toast.success(`${qty}x ${qtyModal.item_name} added`, { duration: 1500 });
    }
    setQtyModal(null);
  };

  return (
    <div className="flex flex-col h-full" data-testid="menu-browse">
      {/* Search */}
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search items..."
            className="w-full h-10 pl-9 pr-4 text-sm border border-gray-300 bg-white focus:border-black focus:ring-1 focus:ring-black outline-none"
            data-testid="menu-search-input" />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="px-4 pb-2 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2" data-testid="category-tabs">
          <button onClick={() => setActiveCat("all")}
            className={`category-tab ${activeCat === "all" ? "active" : ""}`}
            data-testid="cat-tab-all">All</button>
          {categories.map((cat) => (
            <button key={cat.id} onClick={() => setActiveCat(cat.id)}
              className={`category-tab ${activeCat === cat.id ? "active" : ""}`}
              data-testid={`cat-tab-${cat.id}`}>
              {cat.category_name}
            </button>
          ))}
        </div>
      </div>

      {/* Item Grid */}
      <div className="flex-1 overflow-auto px-4 pb-24">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 stagger-children" data-testid="item-grid">
          {items.map((item) => (
            <button key={item.id}
              onClick={() => tapItem(item)}
              onContextMenu={(e) => { e.preventDefault(); longPressItem(item); }}
              className="bg-white border border-gray-200 p-3 text-left hover:border-black transition-colors active:bg-gray-50 animate-fade-in"
              data-testid={`item-${item.id}`}>
              <p className="font-medium text-sm leading-tight">{item.item_name}</p>
              {item.category_name && (
                <span className="inline-block mt-1 text-[9px] font-bold uppercase px-1.5 py-0.5 bg-gray-100 text-gray-500">
                  {item.category_name}
                </span>
              )}
              <p className="font-heading text-lg font-bold mt-2">${item.unit_price.toFixed(2)}</p>
            </button>
          ))}
          {items.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-400">
              {search ? "No items match your search" : "No items available"}
            </div>
          )}
        </div>
      </div>

      {/* Cart Bar */}
      {cartCount > 0 && (
        <div className="cart-bar" data-testid="cart-bar">
          <button onClick={() => navigate("/pos/cart")}
            className="w-full h-12 bg-black text-white flex items-center justify-between px-4 font-medium text-sm active:bg-gray-800"
            data-testid="go-to-cart-button">
            <span className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              {cartCount} item{cartCount !== 1 ? "s" : ""}
            </span>
            <span className="font-heading font-bold text-lg">${cartTotal.toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Quantity Modal */}
      <Dialog open={!!qtyModal} onOpenChange={() => setQtyModal(null)}>
        <DialogContent className="rounded-none border-gray-200 max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">{qtyModal?.item_name}</DialogTitle></DialogHeader>
          <div className="flex items-center justify-center gap-4 py-4">
            <Button variant="outline" size="sm" onClick={() => setQty(Math.max(1, qty - 1))}
              className="h-10 w-10 rounded-none p-0" data-testid="qty-decrease">
              <Minus className="w-4 h-4" />
            </Button>
            <span className="font-heading text-3xl font-black w-16 text-center" data-testid="qty-value">{qty}</span>
            <Button variant="outline" size="sm" onClick={() => setQty(qty + 1)}
              className="h-10 w-10 rounded-none p-0" data-testid="qty-increase">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={confirmQty} className="w-full rounded-none bg-black text-white hover:bg-gray-800 h-11" data-testid="confirm-qty-button">
              Add {qty}x — ${((qtyModal?.unit_price || 0) * qty).toFixed(2)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
